/**
 * Thin client for the audit engine endpoints (/v1/audit/*).
 *
 * Mirrors src/qa/lib/api.ts: same server (VITE_QA_API_URL — the sitemanager
 * App Engine, NOT VITE_API_URL), Firebase Bearer token. Errors carry the HTTP
 * status as the first token of the message so callers can branch on 404/403.
 */
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import type {
  AuditSiteResponse,
  AuditRulesResponse,
  AuditFeedbackVerdict,
  FindingEvidenceResponse,
} from "./types";

const API_URL = import.meta.env.VITE_QA_API_URL || "http://localhost:3100";

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error("401 Not signed in");
  const token = await user.getIdToken();
  return fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

async function errMessage(res: Response): Promise<string> {
  const base = `${res.status} ${res.statusText}`;
  try {
    const d = await res.json();
    const detail = d?.message || d?.error;
    return detail ? `${res.status} ${detail}` : base;
  } catch {
    return base;
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await authedFetch(path, { method: "GET" });
  if (!res.ok) throw new Error(await errMessage(res));
  return res.json() as Promise<T>;
}

export function getAuditSite(orgId: string, propertyId: string): Promise<AuditSiteResponse> {
  return getJson<AuditSiteResponse>(
    `/v1/audit/organizations/${orgId}/properties/${propertyId}`,
  );
}

export function getAuditRules(orgId: string): Promise<AuditRulesResponse> {
  return getJson<AuditRulesResponse>(`/v1/audit/organizations/${orgId}/rules`);
}

export function getFindingEvidence(deficiencyId: string): Promise<FindingEvidenceResponse> {
  return getJson<FindingEvidenceResponse>(`/v1/audit/findings/${deficiencyId}/evidence`);
}

export async function postAuditFeedback(
  deficiencyId: string,
  body: { verdict: AuditFeedbackVerdict; note?: string },
): Promise<void> {
  const res = await authedFetch(`/v1/audit/findings/${deficiencyId}/feedback`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errMessage(res));
}
